import { useCallback, useEffect, useState } from 'react';
import type { Profile, ProfileSession } from '../../shared/types/api';
import { api } from '../lib/api';

const PROFILE_KEY = 'wild-trails-profile-id';
const RECOVERY_KEY = 'wild-trails-recovery-code';
let pendingProfileCreation: Promise<ProfileSession> | null = null;

function createProfileOnce(): Promise<ProfileSession> {
  pendingProfileCreation ??= api.createProfile().finally(() => {
    pendingProfileCreation = null;
  });
  return pendingProfileCreation;
}

interface ProfileState {
  profile: Profile | null;
  recoveryCode: string | null;
  loading: boolean;
  error: string | null;
}

export function useProfile() {
  const [state, setState] = useState<ProfileState>({
    profile: null,
    recoveryCode: localStorage.getItem(RECOVERY_KEY),
    loading: true,
    error: null,
  });

  const saveSession = useCallback(
    (profile: Profile, recoveryCode?: string) => {
      localStorage.setItem(PROFILE_KEY, profile.id);
      if (recoveryCode) localStorage.setItem(RECOVERY_KEY, recoveryCode);
      setState({
        profile,
        recoveryCode: recoveryCode ?? localStorage.getItem(RECOVERY_KEY),
        loading: false,
        error: null,
      });
    },
    [setState],
  );

  useEffect(() => {
    let active = true;
    async function bootstrap() {
      try {
        const storedId = localStorage.getItem(PROFILE_KEY);
        if (storedId) {
          try {
            const profile = await api.getProfile(storedId);
            if (active) saveSession(profile);
            return;
          } catch {
            localStorage.removeItem(PROFILE_KEY);
          }
        }
        const session = await createProfileOnce();
        if (active) saveSession(session.profile, session.recoveryCode);
      } catch (error) {
        if (active) {
          setState((current) => ({
            ...current,
            loading: false,
            error: error instanceof Error ? error.message : 'Wild Trails could not start.',
          }));
        }
      }
    }
    void bootstrap();
    return () => {
      active = false;
    };
  }, [saveSession]);

  const recover = useCallback(
    async (recoveryCode: string) => {
      const session = await api.recoverProfile(recoveryCode);
      localStorage.removeItem(RECOVERY_KEY);
      saveSession(session.profile);
    },
    [saveSession],
  );

  return { ...state, recover };
}
